import React, { PropsWithChildren, useRef } from "react";

import {
  emitRecommenderRequest,
  FromProduct,
  useExperiment,
} from "@catchoftheday/analytics";
import { BoxProps } from "@catchoftheday/cg-components";

import { RECENTLY_VIEWED_TITLE_EXPERIMENT } from "@personalisation/consts";
import {
  RecentlyViewedProductsQuery,
  useRecentlyViewedProductsQuery,
} from "@personalisation/generated/graphql";

import { RecommenderCarousel } from "../RecommenderCarousel";
import {
  getProductTiles,
  getRecommenderTrackingData,
  handleRecommenderQueryUpdate,
  useVisibleRecommenderTiles,
} from "../utils";

export type RecentlyViewedProductsProps = {
  sourceType: number;
  sourceId?: string;
  carouselWrapperProps?: BoxProps;
  Wrapper?: React.FC<PropsWithChildren>;
};

export const RecentlyViewedProducts = ({
  sourceType,
  sourceId,
  carouselWrapperProps,
  Wrapper,
}: RecentlyViewedProductsProps) => {
  const {
    variants: [experiment],
    loading: experimentLoading,
  } = useExperiment([RECENTLY_VIEWED_TITLE_EXPERIMENT]);

  const visibleTiles = useVisibleRecommenderTiles();
  const trackingVariables = useRef({
    offset: 0,
    limit: visibleTiles,
  });

  const { data, loading, error, fetchMore } = useRecentlyViewedProductsQuery({
    variables: {
      limit: visibleTiles,
      offset: 0,
      excludePurchased: true,
      maxDays: 45,
    },
    skip: !visibleTiles,
    ssr: false,
    onCompleted: ({
      recentlyViewed: {
        metaData: {
          preHydrationCount,
          postHydrationCount,
          hydrationErrorCount,
        },
      },
    }) => {
      emitRecommenderRequest({
        source_type: sourceType,
        pre_hydration_product_count: preHydrationCount,
        post_hydration_product_count: postHydrationCount,
        hydration_error_count: hydrationErrorCount,
        page_offset: trackingVariables.current.offset,
        page_limit: trackingVariables.current.limit,
        from_product_ids: null,
        from_product_displayed_price: null,
        from_product_offer_id: null,
      });
    },
  });

  const heading =
    experiment?.bucket === 1
      ? "Pick Up Where You Left Off"
      : experiment?.bucket === 2
      ? "Continue Browsing"
      : "Recently Viewed";

  if (!data?.recentlyViewed.products.length || error || experimentLoading) {
    return null;
  }

  const products = data.recentlyViewed.products;
  const { totalCount, nextItemIndex } = data.recentlyViewed.pagination;

  const fromProduct: FromProduct = {
    from_product_ids: null,
    from_product_displayed_price: null,
    from_product_offer_id: null,
  };

  const tiles = getProductTiles({
    products,
    sourceType,
    fromProduct,
  });
  const trackingData = getRecommenderTrackingData(products, "product");

  const WrapperComponent = Wrapper ?? React.Fragment;

  return (
    <WrapperComponent>
      <RecommenderCarousel
        tiles={tiles}
        heading={heading}
        wrapperProps={carouselWrapperProps}
        sourceType={sourceType}
        sourceId={sourceId}
        trackingData={trackingData}
        fromProduct={fromProduct}
        paginate
        maxTiles={totalCount}
        carouselProps={{
          buttonOffset: [-10, 0],
          onSlideChange: ({ currentIndex, slidesPerView, direction }) => {
            const lastVisibleTile = currentIndex + slidesPerView - 1;
            if (direction === "forward" && !products[lastVisibleTile]) {
              trackingVariables.current = {
                offset: nextItemIndex,
                limit: slidesPerView,
              };
              fetchMore({
                variables: {
                  offset: nextItemIndex,
                },
                updateQuery: (prevResult, { fetchMoreResult }) =>
                  handleRecommenderQueryUpdate<RecentlyViewedProductsQuery>({
                    prevResult,
                    fetchMoreResult,
                    recommenderKey: "recentlyViewed",
                  }),
              });
            }
          },
          disableNavButtons: {
            next: loading,
          },
          touchEnabled: !loading,
        }}
      />
    </WrapperComponent>
  );
};
